import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ContextNode, NavItem } from "@perlab/iframe-bridge";
import { createScopeNode, deleteScopeNode, fetchScopeTree, renameScopeNode, type ScopeNodeResp } from "../api/scopeTree";

/** The four scope levels that can be renamed/deleted (firm/app are not user-managed here). */
export type ScopeKind = "workspace" | "client" | "engagement" | "project";

/** Result of the one-time scope-tree hydration performed when the shell mounts. */
export type ScopeTreeStatus = "loading" | "ready" | "error";

// ---------- Tier 1 V3 §3.0.1 / §3.0.2: ContextKey-addressable + scope ----------
//
// Ported from _PERLab/apps/shell, REFACTORED router-free for the TanStack
// re-implementation: the URL→scope sync + engagement broadcast that previously
// lived in a useLocation()/matchPath() effect here now live in AppShell's
// useUrlScopeSync (which has typed route params). This provider is pure state +
// selectors → unit-testable without a router.
//
//   - ContextKey = `${rg}:${eng}:${projectId}:${appId|"_"}` canonical form.
//   - ContextSlice = per-key snapshot of L0-L3+sub state.
//   - ContextChainAPI gains: contexts Map, currentContextKey,
//     getContext() / setCurrentContext() / upsertContext(), and scope-aware
//     selector hooks below the provider.

export type ContextKey = string;

export interface ContextSlice {
  rgId: string;
  clientId: string;
  engagementId: string;
  projectId: string;
  appId: string | null;
  appTree?: ContextNode;
  pathSegments: string[];
  sideNavItems: NavItem[];
  activeNavId: string | null;
}

/** Build a canonical ContextKey. appId === null collapses to '_'. */
export function makeContextKey(
  rgId: string,
  engagementId: string,
  projectId: string,
  appId: string | null,
): ContextKey {
  return `${rgId}:${engagementId}:${projectId}:${appId ?? "_"}`;
}

// Chain hierarchy (P0 v5.3 — spec-aligned per 15_UAM:306, FEATURE_INVENTORY:172):
//   L0: ResourceGroup (URL slug `:ws`, displayed as "Workspace")
//   L1: Engagement (URL slug `:eng`, data scope per UAM D-UAM-08)
//   L2: Project (work container within an engagement)
//   L3: App
//   L4+: Artifact tree (pushed by the guest sub-app)

// ---------- L0: Workspace (= ResourceGroup) ----------

export interface Workspace {
  id: string;
  label: string;
  envTone?: "production" | "staging" | "development" | "neutral";
}

// ---------- L0.5: Client (first-class scope level per ADR-08 / Decision A) ----------
// The ethical-wall anchor: engagements belong to a client; the client is a navigable
// URL/breadcrumb level (/w/$ws/c/$client/…). Sourced from the BE scope tree (client-type nodes).

export interface Client {
  id: string;       // URL slug
  rgId: string;     // owning workspace
  name: string;
  productUnit?: string;
  businessUnit?: string;
}

// ---------- L1: Engagement (data scope per UAM D-UAM-08) ----------

export type EngagementStatus = "active" | "pending" | "archived";
export type EngagementZone = "client" | "portal" | "public";

export interface Engagement {
  id: string;
  rgId: string;
  clientId: string;   // first-class client this engagement belongs to (ADR-08)
  name: string;
  client: string;     // display name (kept for back-compat / chips)
  status: EngagementStatus;
  lead: string;
  endDate: string;
  zone: EngagementZone;
}

// ---------- L2: Project ----------

// ProjectZone v5.3: renamed "engagement" → "client" (Engagement is now a first-class L1 entity).
export type ProjectZone = "client" | "portal" | "public";

export interface Project {
  id: string;
  code: string;        // Short ref like KEAR-2026-Q4
  label: string;       // Full name
  zone: ProjectZone;
  workspaceId: string; // = resourceGroupId (back-compat name)
  engagementId: string;
}

// Map project zone → ChipDropdown envTone (colored dot).
export function zoneToEnvTone(zone: ProjectZone): "development" | "production" | "staging" {
  return zone === "client" ? "development" : zone === "portal" ? "production" : "staging";
}

// Engagement zone → ChipDropdown envTone.
export function engagementZoneToEnvTone(zone: EngagementZone): "development" | "production" | "staging" {
  return zone === "client" ? "development" : zone === "portal" ? "production" : "staging";
}

// ---------- L3+: App + per-app tree (artifacts) ----------

interface ContextChainState {
  workspaces: Workspace[];
  clients: Client[];
  engagements: Engagement[];
  projects: Project[];
  workspaceId: string;     // = resourceGroupId; URL slug is :ws
  clientId: string;        // first-class client level (ADR-08); URL slug is :client
  engagementId: string;
  projectId: string;
  appId: string | null;
  appTreeByAppId: Record<string, ContextNode>;
  pathSegments: string[];
  sideNavItems: NavItem[];
  activeNavId: string | null;
  // Tier 1 V3 §3.0.1 additions (Slice 1, additive — back-compat preserved).
  contexts: Map<ContextKey, ContextSlice>;
  currentContextKey: ContextKey;
  /** False until the mount scope-tree fetch settles (success OR failure) — lets
   *  consumers tell "still loading" apart from "genuinely empty". */
  scopeTreeLoaded: boolean;
  /** Unlike scopeTreeLoaded, distinguishes a successfully hydrated (possibly empty) tree from a
   *  failed request. Consumers must only infer sealed/missing resources when this is "ready". */
  scopeTreeStatus: ScopeTreeStatus;
  /** Typed failure from the hydrate request. A 401 is a SessionExpiredError; transport/server
   *  failures remain errors so the UI can report an unavailable tree rather than a sealed scope. */
  scopeTreeError: Error | null;
  /** The raw scope_node graph (parent links) as fetched — the NODE-GRAPH source of truth (spec
   *  48.24 §6). The flat clients/engagements/projects arrays are derived VIEWS of this via
   *  mapScopeTree; generic children-by-parent rendering + nodeAncestry read this directly so free
   *  shapes (client under engagement, project under client, …) render correctly. */
  scopeNodes: ScopeNodeResp[];
  /** PERSISTENT TRAIL (operator 2026-07-03): the DEEPEST node of the branch the user is exploring.
   *  The breadcrumb renders this node's full ancestry so clicking an ANCESTOR crumb navigates up
   *  WITHOUT losing the deeper path (you can re-descend). syncTrail keeps it if you click an ancestor,
   *  resets it when you move to a new branch. "" until the first scope resolves. */
  trailNodeId: string;
  /** The app open on the trail's branch — persisted alongside `trailNodeId` so the app crumb survives
   *  clicking an ancestor scope crumb (re-enterable at the trail's deepest node), and drops on a new
   *  branch / tenant switch. "" when no app is on the trail. */
  trailAppId: string;
}

interface ContextChainAPI extends ContextChainState {
  /** Update the trail from the current URL node (+ the open app, if any). Keep the (deeper) trail if
   *  `currentNodeId` is an ancestor of it (clicked up); otherwise the trail becomes the current node
   *  (new branch / descent). The trail app follows: adopt the URL's app, keep it while the trail is kept,
   *  drop it on a new branch. */
  syncTrail: (currentNodeId: string, currentAppId?: string) => void;
  setAppId: (id: string | null) => void;
  setAppTree: (appId: string, root: ContextNode) => void;
  setPathSegments: (segments: string[]) => void;
  setSideNav: (items: NavItem[], activeId?: string | null) => void;
  selectAtLevel: (level: number, segmentId: string) => void;
  // P0 v5.3+: runtime entity creation (powers "+ Create X" in switcher chips).
  addWorkspace: (input: { label: string; envTone?: Workspace["envTone"] }) => Promise<Workspace>;
  addClient: (input: { rgId: string; name: string; productUnit: string; businessUnit: string }) => Promise<Client>;
  addEngagement: (input: { rgId: string; clientId: string; clientName: string; name: string; lead?: string }) => Promise<Engagement>;
  addProject: (input: { workspaceId: string; engagementId: string; code: string; label: string }) => Promise<Project>;
  // §6 node-graph-native create (the ONE "+ Add" door): create ANY type UNDER an arbitrary parent
  // node — free placement (D9-v3). Appends to scopeNodes (the tree source of truth), so the derived
  // arrays + tree update immediately. `confidential` maps to is_barrier (omit ⇒ BE type default).
  createNodeUnder: (input: {
    parentId: string;
    nodeType: "client" | "engagement" | "project";
    name: string;
    confidential?: boolean;
    productUnit?: string;
    businessUnit?: string;
  }) => Promise<ScopeNodeResp>;
  // Rename / delete a scope entity (PATCH/DELETE /scope/nodes). Delete throws if the node has
  // children (BE 409) — the caller surfaces that. Both update the local arrays on success.
  renameNode: (kind: ScopeKind, id: string, name: string) => Promise<void>;
  removeNode: (kind: ScopeKind, id: string) => Promise<void>;
  // Tier 1 V3 §3.0.1 / §3.0.2 — context-key-addressable API.
  getContext: (key?: ContextKey) => ContextSlice | undefined;
  setCurrentContext: (key: ContextKey) => void;
  upsertContext: (key: ContextKey, patch: Partial<ContextSlice>) => void;
  // URL-driven scope sync (the single source of truth) — used by AppShell's
  // useUrlScopeSync to hydrate scope from the typed route params; absent params reset.
  syncScope: (next: { ws: string; client?: string; eng?: string; project?: string; app?: string | null }) => void;
}

const Ctx = createContext<ContextChainAPI | null>(null);

// PRE-HYDRATION PLACEHOLDER ONLY — the slugs the dev/demo scope seed
// (scripts/seed_scope_demo.py) builds, so the index redirect and the initial Map seed have a
// concrete shape to render before the scope tree has arrived. It is NOT a claim that these nodes
// exist: on any environment that does not run that seed they do not, and every walled fetch made
// while they are still in state goes out with a scope id the backend has never heard of. That was
// C1 — a 403 on `reader_scope_node_id=anand-demo` and eight 404s on `p-baseline`, measured in a
// browser against the isolated stack, which seeds `synthetic-dtv-workspace` instead.
//
// `adoptReachableWorkspace` below is the correction the original comment promised and deferred:
// once the tree resolves, a workspace that is not in it is replaced by one the actor can actually
// reach, and the deeper levels — which belonged to the absent tenant — are dropped rather than
// carried into a workspace they do not sit under.
export const DEFAULT_SCOPE = { ws: "anand-demo", client: "acme-co", eng: "e-acme-q4-2026", project: "p-baseline" } as const;
// The placeholder is a WORKSPACE and nothing deeper. `useScopeData` reads at the DEEPEST present
// level, so seeding a client, an engagement AND a project meant every walled read made before the
// URL sync landed went out at `reader_scope_node_id=p-baseline` — a project nobody had navigated
// to, invented by this file. That is where C1's eight 404s came from, and they fired on every
// route including the ones whose URL named no scope at all. A level nobody asserted is not a
// default, it is a claim, so the three deeper ones are empty until something asserts them.
const FIRST_RG = DEFAULT_SCOPE.ws;
const FIRST_ENG = "";
const FIRST_CLIENT = "";
const FIRST_PROJ = "";

// ---------- BE scope_node tree → FE shell arrays ----------
// The breadcrumb levels (workspace/client/engagement/project) derive from the typed scope tree
// by walking parent_id. scope_node also carries the client-only research dimensions; the FE's
// remaining richer attrs (envTone / status / lead / endDate / code / zone) are domain-layer fields
// not stored in the nav tree, so they default here; a later domain-attribute slice fills them.
export interface ScopeArrays {
  firmId: string;
  workspaces: Workspace[];
  clients: Client[];
  engagements: Engagement[];
  projects: Project[];
}

/**
 * Which workspace to land in, given the one currently held and the tree the actor can reach.
 *
 * Returns `null` for "leave it alone" in the three cases where moving somebody would be wrong:
 * the tree is empty (nothing to adopt, and an empty tree is also what a fresh actor sees before
 * anything is provisioned), the held workspace is already reachable, or there is no held
 * workspace at all (the URL sync has not landed yet and will set one).
 *
 * The first workspace is deliberate rather than arbitrary: `mapScopeTree` preserves the order the
 * backend returned, so "first" is the actor's own tree order and not this file's opinion. A later
 * wave that gives an actor a HOME node replaces this function's body and nothing else.
 */
export function adoptReachableWorkspace(
  held: string,
  reachable: ReadonlyArray<{ id: string }>,
): string | null {
  if (!held) return null;
  if (reachable.length === 0) return null;
  if (reachable.some((workspace) => workspace.id === held)) return null;
  return reachable[0]!.id;
}

/** A node's ancestor chain, NEAREST-first (excludes self), by walking `parent_id`. Cycle-guarded. */
function ancestorChain(node: ScopeNodeResp, byId: Map<string, ScopeNodeResp>): ScopeNodeResp[] {
  const chain: ScopeNodeResp[] = [];
  const seen = new Set<string>([node.id]);
  let cur = node.parent_id ? byId.get(node.parent_id) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.push(cur);
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
  }
  return chain;
}

function firstOfType(chain: ScopeNodeResp[], t: ScopeNodeResp["node_type"]): ScopeNodeResp | undefined {
  return chain.find((n) => n.node_type === t);
}

/** One entry of a node's scope ancestry (nearest-first). */
export interface ScopeAncestor {
  id: string;
  node_type: ScopeNodeResp["node_type"];
  name: string;
}

/**
 * The scope ancestry of a node (nearest-first, excludes self) — the SINGLE source for level/scope
 * derivation under FREE placement (spec 48.24 §6). Consumers (URL scope sync, sub-app mount, command
 * palette, facet targeting) resolve "which workspace/client/engagement am I in" via this parent-walk
 * instead of positional URL segments, which collapse when a client nests under an engagement/project.
 */
export function nodeAncestry(nodeId: string, nodes: ScopeNodeResp[]): ScopeAncestor[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const self = byId.get(nodeId);
  if (!self) return [];
  return ancestorChain(self, byId).map((n) => ({ id: n.id, node_type: n.node_type, name: n.name }));
}

/**
 * PERSISTENT TRAIL rule (operator 2026-07-03, the #80 fix). Given the previous trail (deepest node of
 * the branch) and the node you just navigated to, decide the new trail:
 *  · no prior trail, or you landed on the trail node itself → the current node.
 *  · you clicked an ANCESTOR of the trail (navigated UP) → KEEP the deeper trail (so you can re-descend).
 *  · anything else (a sibling, a new branch, or descending to a child) → the current node.
 */
export function nextTrail(prevTrail: string, currentNodeId: string, nodes: ScopeNodeResp[]): string {
  if (!currentNodeId) return prevTrail;
  if (!prevTrail || prevTrail === currentNodeId) return currentNodeId;
  if (nodeAncestry(prevTrail, nodes).some((a) => a.id === currentNodeId)) return prevTrail; // clicked up
  return currentNodeId; // sibling / new branch / descent
}

/**
 * The typed scope scalars a node resolves to under FREE placement (spec 48.24 §6): the node itself for
 * its own type, else the NEAREST ancestor of that type. Drives useUrlScopeSync's /w/$ws/n/$node
 * hydration so the breadcrumb/tree reflect a free-placed node's true position (a client under an
 * engagement resolves client=self + engagement=the host ancestor).
 */
export function deriveScopeScalars(
  nodeId: string,
  nodes: ScopeNodeResp[],
): { client?: string; eng?: string; project?: string } {
  const self = nodes.find((n) => n.id === nodeId);
  const anc = nodeAncestry(nodeId, nodes);
  const nearest = (t: ScopeNodeResp["node_type"]): string | undefined =>
    self?.node_type === t ? nodeId : anc.find((a) => a.node_type === t)?.id;
  return { client: nearest("client"), eng: nearest("engagement"), project: nearest("project") };
}

export function mapScopeTree(nodes: ScopeNodeResp[]): ScopeArrays {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const firm = nodes.find((n) => n.node_type === "firm");
  const workspaces: Workspace[] = [];
  const clients: Client[] = [];
  const engagements: Engagement[] = [];
  const projects: Project[] = [];
  for (const n of nodes) {
    // FREE placement (D9-v3): a node's owning workspace / nearest client / nearest engagement are the
    // TYPED ancestors on its parent chain — never positional (parent-type) assumptions, which collapse
    // when a client nests under an engagement/project, a project under a client, etc. The flat arrays
    // stay the same SHAPE (legacy consumers unchanged) but are now derived by generic parent-walk.
    const anc = ancestorChain(n, byId);
    const ws = firstOfType(anc, "workspace");
    const client = firstOfType(anc, "client");
    const eng = firstOfType(anc, "engagement");
    if (n.node_type === "workspace") {
      workspaces.push({ id: n.id, label: n.name, envTone: "neutral" });
    } else if (n.node_type === "client") {
      clients.push({
        id: n.id,
        rgId: ws?.id ?? "",
        name: n.name,
        productUnit: n.product_unit ?? undefined,
        businessUnit: n.business_unit ?? undefined,
      });
    } else if (n.node_type === "engagement") {
      engagements.push({
        id: n.id, rgId: ws?.id ?? "", clientId: client?.id ?? "", name: n.name,
        client: client?.name ?? "", status: "active", lead: "—", endDate: "—", zone: "client",
      });
    } else if (n.node_type === "project") {
      projects.push({
        id: n.id, code: "", label: n.name, zone: "client",
        workspaceId: ws?.id ?? "", engagementId: eng?.id ?? "",
      });
    }
  }
  return { firmId: firm?.id ?? "", workspaces, clients, engagements, projects };
}

export function ContextChainProvider({ children }: { children: ReactNode }) {
  // Scope-tree arrays — sourced from the BE scope_node tree (R-WIRE 2B). Empty until the
  // fetch-on-mount below hydrates them; the breadcrumb gating already renders placeholders
  // for empty levels, so an unauthenticated/slow boot degrades gracefully (no crash).
  const [scopeNodes, setScopeNodes] = useState<ScopeNodeResp[]>([]);
  const [trailNodeId, setTrailNodeId] = useState<string>("");
  const [trailAppId, setTrailAppId] = useState<string>("");
  const trailNodeIdRef = useRef<string>("");
  // Flat scope arrays DERIVED from scopeNodes — the ONE source of truth (§6). mapScopeTree owns the
  // free-placement ancestry (rgId/clientId/engagementId by parent-walk), so a node created by ANY
  // mutator (the typed adders OR the "+ Add" door's createNodeUnder) shows up in the breadcrumb, tree,
  // and sidebar the instant it lands in scopeNodes — the three can never desync. (Bug fixed: createNode
  // Under appended to scopeNodes but NOT the separate flat useState the breadcrumb read, so a "+ Add"
  // node appeared in the tree but the breadcrumb couldn't level to it.)
  const { firmId, workspaces, clients, engagements, projects } = useMemo(
    () => mapScopeTree(scopeNodes),
    [scopeNodes],
  );

  const [workspaceId, setWorkspaceIdState] = useState<string>(FIRST_RG);
  const [clientId, setClientIdState] = useState<string>(FIRST_CLIENT);
  const [engagementId, setEngagementIdState] = useState<string>(FIRST_ENG);
  const [projectId, setProjectIdState] = useState<string>(FIRST_PROJ);
  const [appId, setAppId] = useState<string | null>(null);
  const [appTreeByAppId, setAppTreeByAppId] = useState<Record<string, ContextNode>>({});
  const [pathSegments, setPathSegments] = useState<string[]>([]);
  const [sideNavItems, setSideNavItems] = useState<NavItem[]>([]);
  const [activeNavId, setActiveNavId] = useState<string | null>(null);

  // Refs mirror state so stable setters (no deps) read latest arrays.
  const engagementsRef = useRef(engagements);
  const projectsRef = useRef(projects);
  useEffect(() => { engagementsRef.current = engagements; }, [engagements]);
  useEffect(() => { projectsRef.current = projects; }, [projects]);

  // B1: the breadcrumb-trail / resume-last-scope memory was RETIRED — it was the one surface that
  // could show a scope deeper than the URL (the sidebar↔breadcrumb desync). Scope now lives in ONE
  // place: the URL → the canonical scalars (below), which both spines read. The URL itself persists
  // across refresh (browser), so "where you were" survives without a separate DISPLAY memory. The
  // account_profile.last_scope column + the profile getLastScope/putLastScope transport are left in
  // place, unused, available to repurpose for a future URL-level resume (land at last scope on login).

  // The firm root id is needed as the parent when creating a new workspace. Sourced from the
  // fetched tree (the firm-type node), so the FE never hard-codes the firm slug.
  const firmIdRef = useRef<string>("");
  // Keep one atomic hydration snapshot so consumers never observe "ready" with a failure (or vice
  // versa). scopeTreeLoaded remains a compatibility projection: it is true after either success or
  // failure, matching its original settled-fetch contract.
  const [scopeTreeHydration, setScopeTreeHydration] = useState<{
    status: ScopeTreeStatus;
    error: Error | null;
  }>({ status: "loading", error: null });
  const scopeTreeStatus = scopeTreeHydration.status;
  const scopeTreeError = scopeTreeHydration.error;
  const scopeTreeLoaded = scopeTreeStatus !== "loading";

  // firmIdRef mirrors the derived firmId so the stable (no-dep) create callbacks read the latest root.
  useEffect(() => { firmIdRef.current = firmId; }, [firmId]);
  // workspaceIdRef mirrors the active workspace so syncScope (no ws in its deps) can detect a
  // tenant/workspace switch and reset the persistent trail (H2 — no prior-tenant crumb bleed).
  const workspaceIdRef = useRef<string>(workspaceId);
  useEffect(() => { workspaceIdRef.current = workspaceId; }, [workspaceId]);
  // Mirror the trail node so external setters (removeNode / syncScope reset) keep the ref honest.
  useEffect(() => { trailNodeIdRef.current = trailNodeId; }, [trailNodeId]);

  // R-WIRE 2B — hydrate the scope arrays from the BE scope_node tree on mount. Single fetch,
  // build the hierarchy via mapScopeTree. Failures remain explicit: a failed request is not an
  // authoritative empty tree and therefore cannot be used to infer sealed/missing resources.
  useEffect(() => {
    let cancelled = false;
    fetchScopeTree()
      .then((nodes) => {
        if (cancelled) return;
        setScopeNodes(nodes);  // the node-graph source of truth (§6); flat arrays + firmId derive from it
        setScopeTreeHydration({ status: "ready", error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        console.warn("[ContextChain] scope-tree hydrate failed:", normalizedError);
        setScopeTreeHydration({ status: "error", error: normalizedError });
      });
    return () => { cancelled = true; };
  }, []);


  const setAppTree = useCallback((id: string, root: ContextNode) => {
    setAppTreeByAppId((prev) => ({ ...prev, [id]: root }));
  }, []);

  const setSideNav = useCallback((items: NavItem[], active?: string | null) => {
    setSideNavItems(items);
    setActiveNavId(active ?? null);
  }, []);

  const selectAtLevel = useCallback((level: number, segmentId: string) => {
    setPathSegments((prev) => {
      const next = prev.slice(0, level);
      next.push(segmentId);
      return next;
    });
  }, []);

  // ---------- Tier 1 V3 §3.0.1 / §3.0.2 — Map + currentContextKey state ----------
  //
  // Seed the Map with exactly one baseline entry so selector hooks return
  // non-empty values on first render (before any URL-driven upsertContext
  // lands from useUrlScopeSync). All 8 ContextSlice fields are populated.
  const INITIAL_CONTEXT_KEY = useMemo(
    () => makeContextKey(FIRST_RG, FIRST_ENG, FIRST_PROJ, null),
    [],
  );
  const [contexts, setContexts] = useState<Map<ContextKey, ContextSlice>>(() => {
    const seed = new Map<ContextKey, ContextSlice>();
    seed.set(INITIAL_CONTEXT_KEY, {
      rgId: FIRST_RG,
      clientId: FIRST_CLIENT,
      engagementId: FIRST_ENG,
      projectId: FIRST_PROJ,
      appId: null,
      pathSegments: [],
      sideNavItems: [],
      activeNavId: null,
    });
    return seed;
  });
  const [currentContextKey, setCurrentContextKeyState] = useState<ContextKey>(INITIAL_CONTEXT_KEY);

  // Ref mirror — stable callbacks read the latest Map without rebinding on
  // every Map mutation (mirrors the engagementsRef/projectsRef pattern above).
  const contextsRef = useRef(contexts);
  useEffect(() => { contextsRef.current = contexts; }, [contexts]);
  const currentContextKeyRef = useRef(currentContextKey);
  useEffect(() => { currentContextKeyRef.current = currentContextKey; }, [currentContextKey]);

  const getContext = useCallback((key?: ContextKey): ContextSlice | undefined => {
    const k = key ?? currentContextKeyRef.current;
    return contextsRef.current.get(k);
  }, []);

  const setCurrentContext = useCallback((key: ContextKey): void => {
    // Pointer move only — does NOT create entries. Callers that need a
    // slice to exist at `key` must call upsertContext first.
    setCurrentContextKeyState(key);
  }, []);

  const upsertContext = useCallback((key: ContextKey, patch: Partial<ContextSlice>): void => {
    setContexts((prev) => {
      const existing = prev.get(key);
      // Default shape when the key is new — every field present per the
      // ContextSlice contract. Patch overrides on top.
      const base: ContextSlice = existing ?? {
        rgId: "",
        clientId: "",
        engagementId: "",
        projectId: "",
        appId: null,
        pathSegments: [],
        sideNavItems: [],
        activeNavId: null,
      };
      const next: ContextSlice = { ...base, ...patch };
      // New Map identity so React re-renders consumers (Maps are mutable
      // but React equality is by reference).
      const out = new Map(prev);
      out.set(key, next);
      return out;
    });
  }, []);

  // PERSISTENT TRAIL (operator 2026-07-03): keep the deeper trail when the user clicks an ANCESTOR
  // crumb (navigate up without losing the path); reset it when they move to a new branch / descend.
  const syncTrail = useCallback<ContextChainAPI["syncTrail"]>((currentNodeId, currentAppId = "") => {
    if (!currentNodeId) return;
    const prev = trailNodeIdRef.current;
    const next = nextTrail(prev, currentNodeId, scopeNodes);
    trailNodeIdRef.current = next;
    setTrailNodeId(next);
    // Trail app: adopt the URL's app when in one; else KEEP it while the scope trail is preserved
    // (you clicked an ancestor crumb — the app stays re-enterable); drop it on a new branch.
    setTrailAppId((prevApp) => (currentAppId ? currentAppId : next === prev ? prevApp : ""));
  }, [scopeNodes]);

  // True once anything has ASSERTED a scope — the URL sync, a breadcrumb, a switcher. Until then
  // the scalars hold a placeholder that nobody chose, and only then may it be replaced.
  const scopeAssertedRef = useRef(false);

  const syncScope = useCallback<ContextChainAPI["syncScope"]>((next) => {
    scopeAssertedRef.current = true;
    // H2: switching workspace/tenant drops the persistent trail (node + app) so a prior tenant's deep
    // crumb path never bleeds into the new one (syncTrail then rebuilds it from the new URL's node).
    if (next.ws !== workspaceIdRef.current) { setTrailNodeId(""); setTrailAppId(""); }
    setWorkspaceIdState(next.ws);
    // The URL is the single source of truth: an ABSENT level param RESETS that level (no stale
    // carry-over from a previously-selected workspace/scope). This kills the cross-tenant stale
    // content + the skipped-Client hierarchy break (a shallow URL no longer keeps deep state).
    setEngagementIdState(next.eng ?? "");
    setProjectIdState(next.project ?? "");
    setAppId(next.app ?? null);
    // Client: explicit from the URL, else derived from the engagement (eng implies client), else cleared.
    const resolvedClient = next.client
      ? next.client
      : next.eng
        ? (engagementsRef.current.find((e) => e.id === next.eng)?.clientId ?? "")
        : "";
    setClientIdState(resolvedClient);
    const key = makeContextKey(next.ws, next.eng ?? "", next.project ?? "", next.app ?? null);
    upsertContext(key, {
      rgId: next.ws,
      clientId: resolvedClient,
      engagementId: next.eng ?? "",
      projectId: next.project ?? "",
      appId: next.app ?? null,
    });
    setCurrentContextKeyState(key);
  }, [upsertContext]);

  // C1 — the landing scope follows the ACTOR's tree, not a build-time constant.
  //
  // Until the tree arrives the scalars hold DEFAULT_SCOPE, which is a placeholder and not a
  // promise: on any environment that did not run `seed_scope_demo.py` those nodes do not exist,
  // and every walled read then carries a scope id the backend has never heard of. Measured in a
  // browser on the isolated stack: `403 GET /brainstorming/sessions?reader_scope_node_id=anand-demo`
  // and eight 404s on `p-baseline`. Nothing corrected it, so the phantom persisted for the life of
  // the page and each surface failed separately with an error that named a permission problem.
  //
  // This runs ONLY on a settled, non-empty tree that does not contain the active workspace, so it
  // is self-terminating: after adoption the workspace IS in the tree and the condition is false. A
  // FAILED fetch is not an authoritative empty tree (the hydrate effect above is explicit about
  // that) and must not move anybody's scope, which is why `ready` is required rather than
  // `scopeTreeLoaded`. The deeper levels go with it: a client/engagement/project from a workspace
  // that is not there cannot be valid under the one that is.
  useEffect(() => {
    if (scopeTreeStatus !== "ready") return;
    // Only while nothing has ASSERTED a scope. A URL that names a workspace is an assertion, and
    // silently relocating a user off one is worse than the denial they came for: `/scope/nodes` is
    // the tree this actor can SEE, and treating "not visible" as "not real" would move somebody
    // out of a workspace that exists whenever the two ever diverge. This is the landing case only
    // — the routes `useUrlScopeSync` returns early on (AppShell.tsx:71, `if (!ws) return`).
    if (scopeAssertedRef.current) return;
    const adopted = adoptReachableWorkspace(workspaceId, workspaces);
    if (!adopted) return;
    console.warn(
      `[ContextChain] workspace "${workspaceId}" is not in this actor's scope tree — ` +
        `landing at "${adopted}" instead. Reachable: ${workspaces.map((w) => w.id).join(", ")}`,
    );
    syncScope({ ws: adopted });
  }, [scopeTreeStatus, workspaceId, workspaces, syncScope]);

  // Create* persist a typed scope node to the BE (server-assigned id) then append the mapped FE
  // row. Domain-only attrs (envTone / lead / code) are kept in the in-session object for UX but
  // are NOT stored in the nav tree — they reset on reload until the domain-attribute slice lands.
  // All create/rename/delete mutate the ONE source of truth (scopeNodes); the flat arrays + firmId
  // re-derive via the useMemo above. Append is idempotent (a create that races the node into scopeNodes
  // won't double-insert). The typed adders still return their mapped FE row for callers that navigate to it.
  const appendNode = (node: ScopeNodeResp) =>
    setScopeNodes((prev) => (prev.some((n) => n.id === node.id) ? prev : [...prev, node]));

  const addWorkspace = useCallback<ContextChainAPI["addWorkspace"]>(async (input) => {
    const node = await createScopeNode({ node_type: "workspace", name: input.label, parent_id: firmIdRef.current || undefined });
    appendNode(node);
    return { id: node.id, label: node.name, envTone: input.envTone ?? "neutral" };
  }, []);

  const addClient = useCallback<ContextChainAPI["addClient"]>(async (input) => {
    const node = await createScopeNode({
      node_type: "client",
      name: input.name,
      parent_id: input.rgId,
      product_unit: input.productUnit,
      business_unit: input.businessUnit,
    });
    appendNode(node);
    return {
      id: node.id,
      rgId: input.rgId,
      name: node.name,
      productUnit: node.product_unit ?? input.productUnit,
      businessUnit: node.business_unit ?? input.businessUnit,
    };
  }, []);

  const addEngagement = useCallback<ContextChainAPI["addEngagement"]>(async (input) => {
    // Parented at the current CLIENT when one is in scope — OR, when clientId is empty, directly at the
    // WORKSPACE: a standalone / cross-client host engagement (client derives NULL, A5/A6). Never null:
    // it always lands under client or workspace, so the hierarchy stays intact.
    const node = await createScopeNode({ node_type: "engagement", name: input.name, parent_id: input.clientId || input.rgId });
    appendNode(node);
    return {
      id: node.id, rgId: input.rgId, clientId: input.clientId, name: node.name,
      client: input.clientName, lead: input.lead ?? "—", status: "active", endDate: "—", zone: "client",
    };
  }, []);

  const addProject = useCallback<ContextChainAPI["addProject"]>(async (input) => {
    const parentEng = engagementsRef.current.find((e) => e.id === input.engagementId);
    const node = await createScopeNode({ node_type: "project", name: input.label, parent_id: input.engagementId });
    appendNode(node);
    return {
      id: node.id, code: input.code, label: node.name, zone: "client",
      workspaceId: parentEng?.rgId ?? input.workspaceId, engagementId: input.engagementId,
    };
  }, []);

  const createNodeUnder = useCallback<ContextChainAPI["createNodeUnder"]>(async (input) => {
    const node = await createScopeNode({
      node_type: input.nodeType,
      name: input.name,
      parent_id: input.parentId,
      ...(input.confidential === undefined ? {} : { is_barrier: input.confidential }),
      ...(input.nodeType === "client"
        ? { product_unit: input.productUnit, business_unit: input.businessUnit }
        : {}),
    });
    appendNode(node);  // the flat arrays + tree derive from scopeNodes (§6) — breadcrumb sees it at once
    return node;
  }, []);

  const renameNode = useCallback<ContextChainAPI["renameNode"]>(async (_kind, id, name) => {
    await renameScopeNode(id, name);
    setScopeNodes((prev) => prev.map((n) => (n.id === id ? { ...n, name } : n)));
  }, []);

  const removeNode = useCallback<ContextChainAPI["removeNode"]>(async (kind, id) => {
    await deleteScopeNode(id, kind === "workspace");
    // H2: if the deleted node IS the trail (or an ancestor of it), the trail now points at a vanished
    // node → blank breadcrumb. Reset the trail to the deleted node's parent (its nearest survivor) so the
    // crumb bar stays anchored. Computed against the pre-delete graph (the node still exists here).
    const parentOfDeleted = scopeNodes.find((n) => n.id === id)?.parent_id ?? "";
    setTrailNodeId((prevTrail) => {
      if (!prevTrail) return prevTrail;
      if (prevTrail === id || nodeAncestry(prevTrail, scopeNodes).some((a) => a.id === id)) {
        setTrailAppId(""); // the branch the trail app lived on is gone
        return parentOfDeleted;
      }
      return prevTrail;
    });
    setScopeNodes((prev) => prev.filter((n) => n.id !== id));
  }, [scopeNodes]);

  const value = useMemo<ContextChainAPI>(
    () => ({
      workspaces,
      clients,
      engagements,
      projects,
      workspaceId,
      clientId,
      engagementId,
      projectId,
      appId,
      appTreeByAppId,
      pathSegments,
      sideNavItems,
      activeNavId,
      // Tier 1 V3 §3.0.1 fields.
      contexts,
      currentContextKey,
      scopeTreeLoaded,
      scopeTreeStatus,
      scopeTreeError,
      scopeNodes,
      trailNodeId,
      trailAppId,
      syncTrail,
      setAppId,
      setAppTree,
      setPathSegments,
      setSideNav,
      selectAtLevel,
      addWorkspace,
      addClient,
      addEngagement,
      addProject,
      createNodeUnder,
      renameNode,
      removeNode,
      // Tier 1 V3 §3.0.1 API.
      getContext,
      setCurrentContext,
      upsertContext,
      syncScope,
    }),
    [
      workspaces, clients, engagements, projects,
      workspaceId, clientId, engagementId, projectId, appId, appTreeByAppId, pathSegments, sideNavItems, activeNavId,
      contexts, currentContextKey, scopeTreeLoaded, scopeTreeStatus, scopeTreeError,
      scopeNodes, trailNodeId, trailAppId, syncTrail,
      setAppTree, setSideNav, selectAtLevel,
      addWorkspace, addClient, addEngagement, addProject, createNodeUnder, renameNode, removeNode,
      getContext, setCurrentContext, upsertContext, syncScope,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useContextChain(): ContextChainAPI {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useContextChain must be used within <ContextChainProvider>");
  return ctx;
}

// ---------- Tier 1 V3 §3.0.2 — ContextKeyScope + scope-aware selector hooks ----------
//
// Tier 1 today: one ContextChainProvider, no scope providers in the tree. Every
// scope-aware selector resolves to `currentContextKey` — identical to reading
// the scalar fields off useContextChain() directly.
//
// Tier 3 tomorrow (split-pane): wrap each pane in <ContextKeyScope value={paneKey}>.
//
// Selector hooks return safe defaults ("" / null / undefined) when the resolved
// key is missing from the Map.

const ContextKeyScopeContext = createContext<ContextKey | null>(null);

export function ContextKeyScope({
  value,
  children,
}: {
  value: ContextKey;
  children: ReactNode;
}) {
  return <ContextKeyScopeContext.Provider value={value}>{children}</ContextKeyScopeContext.Provider>;
}

/**
 * Resolve the active ContextKey for the calling component.
 *
 * Resolution order per §3.0.2:
 *   1. Nearest <ContextKeyScope value={...}> ancestor (Tier 3 pane scope).
 *   2. ContextChainState.currentContextKey (Tier 1 single-tree default).
 */
export function useResolvedContextKey(): ContextKey {
  const scoped = useContext(ContextKeyScopeContext);
  const chain = useContextChain();
  return scoped ?? chain.currentContextKey;
}

/**
 * Where the scope tree stands: "loading" until `/scope/nodes` settles, then "ready" or "error".
 *
 * A selector rather than a `useContextChain()` call at the consumer, because the consumers that
 * need it (useScopeData) need ONLY this, and pulling the whole chain API in makes them harder to
 * test in isolation than the value warrants.
 */
export function useScopeTreeStatus(): ScopeTreeStatus {
  return useContextChain().scopeTreeStatus;
}

/** Scope-aware workspaceId selector (= the ContextKey's rgId, its first segment). */
export function useWorkspaceId(): string {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key)?.rgId ?? "";
}

/** Scope-aware clientId selector. Empty string when slice is absent. */
export function useClientId(): string {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key)?.clientId ?? "";
}

/** Scope-aware engagementId selector. Empty string when slice is absent. */
export function useEngagementId(): string {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key)?.engagementId ?? "";
}

/** Scope-aware projectId selector. Empty string when slice is absent. */
export function useProjectId(): string {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key)?.projectId ?? "";
}

/** Scope-aware appId selector. Null when slice is absent or appId unset. */
export function useAppId(): string | null {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key)?.appId ?? null;
}

/** Scope-aware full-slice selector. Undefined when slice is absent. */
export function useContextSlice(): ContextSlice | undefined {
  const key = useResolvedContextKey();
  const chain = useContextChain();
  return chain.contexts.get(key);
}

// Walks the tree along pathSegments, returning each level's node + its siblings.
export interface ResolvedLevel {
  level: number;
  node: ContextNode;
  siblings: ContextNode[];
}

export function resolveChain(root: ContextNode, segments: string[]): ResolvedLevel[] {
  const out: ResolvedLevel[] = [];
  let currentChildren: ContextNode[] = root.children ?? [];
  for (let i = 0; i < segments.length; i++) {
    const segId = segments[i];
    const node = currentChildren.find((c) => c.id === segId);
    if (!node) break;
    out.push({ level: i, node, siblings: currentChildren });
    currentChildren = node.children ?? [];
  }
  return out;
}
